import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Box, Typography, Tooltip, Snackbar, Alert, useTheme } from '@mui/material';
import { Close as CloseIcon, ContentCopy as ContentCopyIcon, Facebook as FacebookIcon, Twitter as TwitterIcon, LinkedIn as LinkedInIcon, MailOutline as MailOutlineIcon } from '@mui/icons-material';

function ShareDialog({ open, onClose, title, storyUrl, isOwnStory = false }) {
  const theme = useTheme();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCopyLink = () => {
    if (storyUrl) {
      navigator.clipboard.writeText(storyUrl)
        .then(() => {
          showSnackbar('Link copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          showSnackbar('Failed to copy link.');
        });
    }
  };
  
  // --- UPDATED Share Text Construction (URL back in main body) ---
  const firstLine = isOwnStory 
      ? "Check out this story I just made on Make It Mindful!" 
      : "Check out this story I just read on Make It Mindful!";

  const shareSubject = `${firstLine}: "${title || 'Untitled Story'}"`;
  // Include URL directly in the body with \r\n line breaks
  const shareTextBody = 
`${firstLine}

Title: ${title || 'Untitled Story'}

Read the story here and comment what you think about it.${storyUrl ? `\r\n\r\n${storyUrl}` : ''}`; // Add URL if it exists

  // Encoded variables
  const encodedShareText = encodeURIComponent(shareTextBody);
  const encodedShareSubject = encodeURIComponent(shareSubject);
  const encodedShareUrl = encodeURIComponent(storyUrl || '');

  // --- Social/Email functions use updated encoded text ---
  const shareToSocial = (platform) => {
    let url = '';
    
    switch (platform) {
      case 'facebook':
        // FB quote now uses the full text including the URL
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}&quote=${encodedShareText}`;
        break;
      case 'twitter':
        // Twitter text now uses the full text including the URL
        url = `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedShareText}`;
        break;
      case 'linkedin':
        // LinkedIn still primarily uses the URL
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`;
        break;
      default:
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shareViaEmail = () => {
    // Email body now directly uses the encoded text which includes the URL
    window.location.href = `mailto:?subject=${encodedShareSubject}&body=${encodedShareText}`;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        aria-labelledby="share-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="share-dialog-title">
          Share "{title || 'Untitled Story'}"
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {storyUrl && (
             <>
               <Typography variant="body2" gutterBottom>
                 Share this story using the link:
               </Typography>
               <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                 <TextField
                   fullWidth
                   variant="outlined"
                   size="small"
                   value={storyUrl}
                   InputProps={{ readOnly: true }}
                   aria-label="Story Link"
                 />
                 <Tooltip title="Copy Link">
                   <IconButton onClick={handleCopyLink} color="primary">
                     <ContentCopyIcon />
                   </IconButton>
                 </Tooltip>
               </Box>
             </>
           )}
          
          <Typography variant="body2" gutterBottom>
            Or share directly on:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 1 }}>
            <Tooltip title="Share on Facebook">
              <IconButton onClick={() => shareToSocial('facebook')} sx={{ color: '#4267B2' }} disabled={!storyUrl}>
                <FacebookIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on Twitter">
              <IconButton onClick={() => shareToSocial('twitter')} sx={{ color: '#1DA1F2' }} /* Twitter doesn't strictly need the URL to share text */ >
                <TwitterIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on LinkedIn">
              <IconButton onClick={() => shareToSocial('linkedin')} sx={{ color: '#0A66C2' }} disabled={!storyUrl}>
                <LinkedInIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share via Email">
              <IconButton onClick={shareViaEmail} sx={{ color: theme.palette.text.secondary }}>
                <MailOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ShareDialog; 